import {
  actorHasFinanceAccess,
  resolveFinanceOrganizationIdFromActor,
} from "./nexoraOpsAccess";

/** Owner-facing account presets (maps to instrument + purpose + class). */
export const FINANCE_ACCOUNT_PRESETS = Object.freeze([
  {
    key: "operating",
    label: "Operating Chequing",
    instrument: "depository",
    purpose: "operating",
    class: "asset",
  },
  {
    key: "savings",
    label: "Savings",
    instrument: "depository",
    purpose: "savings",
    class: "asset",
  },
  {
    key: "tax_reserve",
    label: "Tax Reserve",
    instrument: "depository",
    purpose: "tax_reserve",
    class: "asset",
  },
  {
    key: "credit_card",
    label: "Credit Card",
    instrument: "credit_card",
    purpose: "other",
    class: "liability",
  },
  {
    key: "loan",
    label: "Loan",
    instrument: "loan",
    purpose: "other",
    class: "liability",
  },
]);

/** @deprecated Use FINANCE_ACCOUNT_PRESETS */
export const FINANCE_ACCOUNT_TYPES = Object.freeze(
  FINANCE_ACCOUNT_PRESETS.map((p) => p.key),
);

export const FINANCE_ACCOUNT_TYPE_LABELS = Object.freeze(
  Object.fromEntries(FINANCE_ACCOUNT_PRESETS.map((p) => [p.key, p.label])),
);

const ACCOUNT_SELECT =
  "id, organization_id, name, institution, instrument, purpose, class, type, currency, opening_balance, opening_balance_on, opening_balance_frozen, active, created_at, updated_at";

function requireClient(client) {
  if (!client) {
    throw new Error("Supabase client is required.");
  }
}

export function resolveAccountPreset(keyOrFields = {}) {
  if (typeof keyOrFields === "string") {
    return (
      FINANCE_ACCOUNT_PRESETS.find((p) => p.key === keyOrFields) ||
      FINANCE_ACCOUNT_PRESETS[0]
    );
  }

  if (keyOrFields.instrument && keyOrFields.purpose && keyOrFields.class) {
    return {
      key: keyOrFields.preset || "custom",
      label: keyOrFields.label || "Account",
      instrument: keyOrFields.instrument,
      purpose: keyOrFields.purpose,
      class: keyOrFields.class,
    };
  }

  const presetKey = keyOrFields.preset || keyOrFields.type || "operating";
  return (
    FINANCE_ACCOUNT_PRESETS.find((p) => p.key === presetKey) ||
    FINANCE_ACCOUNT_PRESETS[0]
  );
}

export function accountPresetKey(account) {
  if (!account) {
    return "operating";
  }
  const match = FINANCE_ACCOUNT_PRESETS.find(
    (p) =>
      p.instrument === account.instrument &&
      p.purpose === account.purpose &&
      p.class === account.class,
  );
  if (match) {
    return match.key;
  }
  // Legacy Phase 1 type
  if (account.type === "chequing") return "operating";
  if (account.type === "tax") return "tax_reserve";
  if (account.type && FINANCE_ACCOUNT_TYPE_LABELS[account.type]) {
    return account.type;
  }
  return "operating";
}

function normalizeAccountPayload(input = {}) {
  const name = String(input.name || "").trim();
  const institution = String(input.institution || "").trim() || null;
  const currency =
    String(input.currency || "CAD").trim().toUpperCase() || "CAD";
  const openingBalance = Number(input.opening_balance);
  const active = input.active !== false;
  const preset = resolveAccountPreset(input);

  if (!name) {
    throw new Error("Account name is required.");
  }

  if (!Number.isFinite(openingBalance)) {
    throw new Error("Opening balance must be a number.");
  }

  return {
    name,
    institution,
    instrument: preset.instrument,
    purpose: preset.purpose,
    class: preset.class,
    // Keep legacy type populated for readability during transition
    type:
      preset.key === "operating"
        ? "chequing"
        : preset.key === "tax_reserve"
          ? "tax"
          : preset.key,
    currency,
    opening_balance: openingBalance,
    opening_balance_on: input.opening_balance_on || undefined,
    active,
  };
}

export async function listFinanceAccounts(
  client,
  { includeInactive = true } = {},
) {
  requireClient(client);

  let query = client
    .from("finance_accounts")
    .select(ACCOUNT_SELECT)
    .order("active", { ascending: false })
    .order("name", { ascending: true });

  if (!includeInactive) {
    query = query.eq("active", true);
  }

  const { data, error } = await query;
  if (error) {
    throw error;
  }
  return data || [];
}

/**
 * Create a finance account stamped with the actor's organization.
 */
export async function createFinanceAccount(client, input, actor = null) {
  requireClient(client);

  const allowed = await actorHasFinanceAccess(client);
  if (!allowed) {
    throw new Error("Finance module is not available for this organization.");
  }

  const payload = normalizeAccountPayload(input);
  const organizationId =
    input.organization_id ||
    resolveFinanceOrganizationIdFromActor(actor) ||
    actor?.organization_id;

  if (!organizationId) {
    throw new Error("organization_id is required to create a finance account.");
  }

  const insertRow = {
    ...payload,
    organization_id: organizationId,
  };

  if (!insertRow.opening_balance_on) {
    insertRow.opening_balance_on = new Date().toISOString().slice(0, 10);
  }

  const { data, error } = await client
    .from("finance_accounts")
    .insert(insertRow)
    .select(ACCOUNT_SELECT)
    .single();

  if (error) {
    throw error;
  }
  return data;
}

export async function updateFinanceAccount(client, accountId, input) {
  requireClient(client);

  if (!accountId) {
    throw new Error("Account id is required.");
  }

  const allowed = await actorHasFinanceAccess(client);
  if (!allowed) {
    throw new Error("Finance module is not available for this organization.");
  }

  const payload = normalizeAccountPayload(input);
  // Never unfreeze / rewrite freeze flag from client normalize
  delete payload.opening_balance_frozen;

  const { data, error } = await client
    .from("finance_accounts")
    .update(payload)
    .eq("id", accountId)
    .select(ACCOUNT_SELECT)
    .single();

  if (error) {
    throw error;
  }
  return data;
}

export async function deactivateFinanceAccount(client, accountId) {
  requireClient(client);

  if (!accountId) {
    throw new Error("Account id is required.");
  }

  const allowed = await actorHasFinanceAccess(client);
  if (!allowed) {
    throw new Error("Finance module is not available for this organization.");
  }

  const { data, error } = await client
    .from("finance_accounts")
    .update({ active: false })
    .eq("id", accountId)
    .select(ACCOUNT_SELECT)
    .single();

  if (error) {
    throw error;
  }
  return data;
}

export async function reactivateFinanceAccount(client, accountId) {
  requireClient(client);

  if (!accountId) {
    throw new Error("Account id is required.");
  }

  const allowed = await actorHasFinanceAccess(client);
  if (!allowed) {
    throw new Error("Finance module is not available for this organization.");
  }

  const { data, error } = await client
    .from("finance_accounts")
    .update({ active: true })
    .eq("id", accountId)
    .select(ACCOUNT_SELECT)
    .single();

  if (error) {
    throw error;
  }
  return data;
}

export function formatFinanceAccountType(accountOrKey) {
  if (accountOrKey && typeof accountOrKey === "object") {
    const key = accountPresetKey(accountOrKey);
    return FINANCE_ACCOUNT_TYPE_LABELS[key] || accountOrKey.name || "—";
  }
  return FINANCE_ACCOUNT_TYPE_LABELS[accountOrKey] || accountOrKey || "—";
}

export function formatOpeningBalance(amount, currency = "CAD") {
  const value = Number(amount);
  const safe = Number.isFinite(value) ? value : 0;
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency || "CAD",
    }).format(safe);
  } catch {
    return `${safe.toFixed(2)} ${currency || "CAD"}`;
  }
}
